import { useEffect, useState, useMemo, useCallback } from 'react';
import { Event } from 'nostr-tools';
import { useAdmin } from '../contexts/AdminContext';
import { getNostrPool } from '../lib/nostrPool';
import { RELAYS } from '../config/constants';
import { useGlobalModeration } from './useGlobalModeration';

export const useNotifications = (pubkey: string | null) => {
    const [events, setEvents] = useState<Event[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isLinking, setIsLinking] = useState(false);
    const pool = getNostrPool();
    const { settings } = useAdmin();
    const { blockedEventIds, blockedPubkeys } = useGlobalModeration();

    const loadMore = useCallback(async () => {
        if (!pubkey || isLinking || isLoading) return;

        const oldestEvent = events[events.length - 1];
        if (!oldestEvent) return;

        setIsLinking(true);

        const filter: any = {
            kinds: [1, 6, 7, 9735],
            '#p': [pubkey],
            until: oldestEvent.created_at - 1,
            limit: 20
        };

        const moreEvents: Event[] = [];
        const sub = pool.subscribeMany(RELAYS, filter, {
            onevent(event: Event) {
                if (event.pubkey === pubkey) return; // Filter self-interactions
                moreEvents.push(event);
            },
            oneose() {
                if (moreEvents.length > 0) {
                    setEvents(prev => {
                        const combined = [...prev, ...moreEvents];
                        // Deduplicate
                        const unique = Array.from(new Map(combined.map(e => [e.id, e])).values());
                        return unique.sort((a, b) => b.created_at - a.created_at);
                    });
                }
                setIsLinking(false);
                sub.close();
            }
        });

        setTimeout(() => {
            setIsLinking(false);
            sub.close();
        }, 5000);
    }, [pubkey, events, isLinking, isLoading, pool]);

    useEffect(() => {
        if (!pubkey) return;

        setIsLoading(true);
        setEvents([]);

        const filter: any = {
            kinds: [1, 6, 7, 9735],
            '#p': [pubkey],
            limit: 20
        };

        const timeout = setTimeout(() => {
            setIsLoading(false);
        }, 5000);

        const sub = pool.subscribeMany(RELAYS, filter, {
            onevent(event: Event) {
                clearTimeout(timeout);
                // Filter self-interactions immediately
                if (event.pubkey === pubkey) return;

                setEvents((prev) => {
                    const exists = prev.some((e) => e.id === event.id);
                    if (exists) return prev;
                    return [...prev, event].sort((a, b) => b.created_at - a.created_at);
                });
            },
            oneose() {
                clearTimeout(timeout);
                setIsLoading(false);
            },
        });

        return () => {
            clearTimeout(timeout);
            sub.close();
        };
    }, [pubkey]);

    // Reactive Filtering
    const filteredEvents = useMemo(() => {
        return events.filter(e => {
            // Global Admin Moderation Filter
            if (blockedEventIds.has(e.id)) return false;
            if (blockedPubkeys.has(e.pubkey)) return false;

            // Local Mute Filter
            if (settings.mutedUsers.includes(e.pubkey)) return false;

            // 5. Isolate Community Filter (Strict)
            if (settings.isolateCommunity) {
                // Allow Zaps (Kind 9735) and Likes (Kind 7)
                // Likes often don't usually carry topic tags, and spamming them is less common than text mentions.
                if (e.kind === 9735 || (e.kind === 7 && e.content !== '-')) return true;

                // For Text (1), Repost (6)
                // Check for '#t' tags
                const tags = e.tags.filter(t => t[0] === 't').map(t => t[1].toLowerCase());
                const hasTopic = tags.includes('mavililer') || tags.includes('mavililersozluk') || tags.includes('mavililer-sozluk');

                // Also check content for hashtag if tags are missing (fallback)
                const hasContentTag = e.content.toLowerCase().includes('#mavililer');

                if (!hasTopic && !hasContentTag) return false;
            }

            return true;
        })
            .map(e => {
                // Deduplicate logic or other mapping if needed
                return e;
            });
    }, [events, blockedEventIds, blockedPubkeys, settings.mutedUsers, settings.isolateCommunity]);

    // Read Status Management
    const [lastReadAt, setLastReadAt] = useState<number>(() => {
        if (!pubkey) return Math.floor(Date.now() / 1000);
        const stored = localStorage.getItem(`mavililer_last_read_${pubkey}`);
        return stored ? parseInt(stored) : 0;
    });

    const markAsRead = useCallback(() => {
        if (!pubkey) return;
        const now = Math.floor(Date.now() / 1000);
        setLastReadAt(now);
        localStorage.setItem(`mavililer_last_read_${pubkey}`, now.toString());
    }, [pubkey]);

    const unreadCount = useMemo(() => {
        if (!events.length) return 0;
        return filteredEvents.filter(e => e.created_at > lastReadAt).length;
    }, [filteredEvents, lastReadAt]);

    return { events: filteredEvents, isLoading, loadMore, isLinking, unreadCount, markAsRead, lastReadAt };
};
